/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import TrainerCard from "@/components/TrainerCard";
import ActivityFeed from "@/components/ActivityFeed";
import RecruitModal from "@/components/RecruitModal";
import TrainerDetailModal from "@/components/TrainerDetailModal";
import ForceActionModal from "@/components/ForceActionModal";
import { describeCondition } from "@/components/RecruitModal";
import { UserPlus, Activity, Lock, Unlock, Map, Gem } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NOTABLE_EVENTS = new Set([
  "pokemon_level_up", "evolution", "badge", "level_up",
  "legendary_catch", "league_win", "charm_available",
  "region_unlock", "no_pokeball", "stamina_low", "train_fail", "charm_used",
]);

export default function Dashboard() {
  const { user } = useAuth();
  const [trainers, setTrainers] = useState([]);
  const [events, setEvents] = useState([]);
  const [pokedex, setPokedex] = useState({ seen: 0, caught: 0, total: 151 });
  const [ref, setRef] = useState({ locations: {}, gyms: [] });
  const [prog, setProg] = useState(null);
  const [charms, setCharms] = useState({ pending: [], issued_regions: [] });
  const [recruitOpen, setRecruitOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailTab, setDetailTab] = useState("team");
  const [forceAction, setForceAction] = useState(null); // {id, action}
  const [charmAssigning, setCharmAssigning] = useState(false);
  const seenEventIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [t, e, p, r, pr, ch] = await Promise.all([
        api.get("/trainers"),
        api.get("/events", { params: { limit: 30 } }),
        api.get("/pokedex"),
        api.get("/ref"),
        api.get("/progression"),
        api.get("/charms"),
      ]);
      setTrainers(t.data);
      setEvents(e.data);
      setPokedex({ seen: p.data.seen, caught: p.data.caught, total: p.data.total });
      setRef(r.data);
      setProg(pr.data);
      setCharms(ch.data);
      // Toast notifications for new notable events
      if (!initializedRef.current) {
        // Seed seen with current events on first load (no spam)
        e.data.forEach(ev => seenEventIdsRef.current.add(ev.id));
        initializedRef.current = true;
      } else {
        // events come desc by ts. Iterate from oldest (end) to newest so toasts appear in order
        for (let i = e.data.length - 1; i >= 0; i--) {
          const ev = e.data[i];
          if (seenEventIdsRef.current.has(ev.id)) continue;
          seenEventIdsRef.current.add(ev.id);
          if (NOTABLE_EVENTS.has(ev.type)) {
            const isError = ["no_pokeball", "defeat", "train_fail", "league_loss"].includes(ev.type);
            const isWarn = ["stamina_low"].includes(ev.type);
            const isSuccess = ["evolution", "pokemon_level_up", "legendary_catch", "league_win", "badge", "charm_available", "charm_used", "region_unlock", "level_up"].includes(ev.type);
            if (isError) {
              toast.error(ev.message);
            } else if (isWarn) {
              toast.warning ? toast.warning(ev.message) : toast(ev.message, { icon: "💤" });
            } else if (isSuccess) {
              toast.success(ev.message);
            } else {
              toast.message(ev.message);
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const setStrategy = async (trainerId, strategy) => {
    // Route-picker for actions that target a route.
    if (["explore", "capture", "train"].includes(strategy)) {
      setForceAction({ id: trainerId, action: strategy });
      return;
    }
    try {
      await api.post(`/trainers/${trainerId}/strategy`, { strategy });
      if (strategy === "rest") toast.success("Repos forcé — placé en prochaine action.");
      await load();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dismiss = async (id) => {
    if (!window.confirm("Renvoyer ce dresseur ? Son équipe sera perdue.")) return;
    try { await api.delete(`/trainers/${id}`); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const assignCharm = async (trainerId) => {
    setCharmAssigning(true);
    try {
      const { data } = await api.post(`/charms/assign/${trainerId}`);
      toast.success(`Charme Chromatique (${data.region_id}) attribué !`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCharmAssigning(false); }
  };

  const totalBadges = trainers.reduce((s, t) => s + (t.badges?.length || 0), 0);
  const leagueWinners = trainers.filter(t => t.league_status === "completed").length;
  const usedSlots = trainers.length;
  const maxSlots = prog?.unlocked_slot_count ?? 1;
  const nextSlot = prog?.slots?.find(s => !s.unlocked);

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="dashboard-page">
      {/* Collégiale banner */}
      <div
        className="col-span-12 relative overflow-hidden border border-[#2B2B2B] h-32 md:h-40 bg-cover bg-center"
        style={{ backgroundImage: "url(/img/lore/collegiale.png)" }}
        data-testid="dashboard-banner"
      >
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(90deg, rgba(26,26,26,0.75) 0%, rgba(26,26,26,0.15) 60%, rgba(26,26,26,0) 100%)" }}
        />
        <div className="relative z-10 p-4 md:p-6 flex flex-col justify-end h-full">
          <div className="overline text-[10px] text-[#F4EFE6]/80">Collégiale de Bourg-Palette</div>
          <h1 className="font-serif-display text-2xl md:text-4xl leading-none mt-1 text-[#F4EFE6] drop-shadow-md">
            Bureau du Prof. {user?.professor_name || "…"}
          </h1>
        </div>
      </div>

      {/* Stats row */}
      <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Slots dresseurs" value={`${usedSlots}/${maxSlots}`} sub={nextSlot ? `Slot ${nextSlot.slot} verrouillé` : "Tous débloqués"} testid="stat-trainers"/>
        <StatCard label="Pokédex" value={`${pokedex.caught}`} sub={`vus ${pokedex.seen} · total ${pokedex.total}`} testid="stat-pokedex"/>
        <StatCard label="Badges collectés" value={totalBadges} sub="cumulés par tous les dresseurs" testid="stat-badges"/>
        <StatCard label="Maîtres de la Ligue" value={leagueWinners} sub="champions formés" testid="stat-masters"/>
      </div>

      {/* Activity feed */}
      <section className="col-span-12 lg:col-span-7 panel p-0 flex flex-col min-h-[420px]" data-testid="dashboard-activity">
        <header className="flex items-center justify-between px-4 py-2 border-b border-[#2B2B2B] bg-[#EAE4D9] shrink-0">
          <div className="flex items-center gap-2">
            <Activity size={14}/>
            <h2 className="overline">Journal d&apos;activité · temps réel</h2>
          </div>
          <span className="font-mono text-[10px] text-[#4A4A4A]">{events.length} entrées</span>
        </header>
        <div className="flex-1 overflow-auto">
          <ActivityFeed events={events} trainers={trainers}/>
        </div>
      </section>

      {/* Right column */}
      <section className="col-span-12 lg:col-span-5 space-y-4">
        <div className="panel p-3" data-testid="dashboard-trainers">
          <div className="flex justify-between items-center mb-3">
            <h2 className="overline">Équipe au labo</h2>
            <span className="font-mono text-xs text-[#4A4A4A]">{trainers.length}</span>
          </div>
          {trainers.length === 0 ? (
            <div className="text-xs text-[#7A7A7A] font-mono p-3 text-center">
              Aucun dresseur. Cliquez sur « Recruter ».
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-[640px] overflow-auto">
              {trainers.map((t) => (
                <TrainerCard
                  key={t.id}
                  trainer={t}
                  locationsRef={ref.locations}
                  gymsRef={ref.gyms}
                  onStrategy={setStrategy}
                  onDismiss={dismiss}
                  onOpenDetail={(tid) => { setDetailTab("team"); setDetailId(tid); }}
                  onOpenBag={(tid) => { setDetailTab("bag"); setDetailId(tid); }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="panel-strong p-4" data-testid="recruit-panel">
          <div className="overline">Recrutement</div>
          <h2 className="font-serif-display text-2xl leading-none mt-1">Engager un dresseur</h2>
          <p className="text-xs text-[#4A4A4A] mt-2 mb-3">
            {usedSlots < maxSlots
              ? `${maxSlots - usedSlots} slot(s) disponible(s). Le candidat choisira son starter.`
              : maxSlots >= 12
                ? "Équipe complète."
                : nextSlot
                  ? `Prochain slot : ${nextSlot.label}`
                  : "Tous les slots sont remplis."}
          </p>
          <button
            data-testid="recruit-btn"
            className="btn-ink btn-leaf"
            onClick={() => setRecruitOpen(true)}
            disabled={usedSlots >= maxSlots}
          >
            <UserPlus size={14}/> Recruter un dresseur
          </button>
        </div>

        {/* Progression panel */}
        {prog && (
          <div className="panel p-3" data-testid="progression-panel">
            <div className="flex justify-between items-center mb-2">
              <h2 className="overline">Progression du labo</h2>
              <span className="font-mono text-[10px] text-[#4A4A4A]">{usedSlots}/{maxSlots}</span>
            </div>
            <div className="grid grid-cols-6 gap-1 mb-2">
              {prog.slots.map((s) => (
                <div
                  key={s.slot}
                  data-testid={`slot-${s.slot}`}
                  title={s.label}
                  className={`aspect-square border flex items-center justify-center font-mono text-[10px] ${
                    s.unlocked ? "bg-[#2E4F32] text-[#F4EFE6] border-[#1A1A1A]" : "bg-[#EAE4D9] text-[#7A7A7A] border-[#7A7A7A]"
                  }`}
                >
                  {s.slot}
                </div>
              ))}
            </div>
            {maxSlots >= 12 && !nextSlot ? (
              <div className="text-[11px] font-mono text-[#2E4F32] mt-2 font-bold" data-testid="all-slots-unlocked">
                ✓ Équipe complète — 12 dresseurs débloqués
              </div>
            ) : nextSlot && (
              <div className="text-[11px] font-mono text-[#4A4A4A] mt-2">
                <strong className="text-[#1A1A1A]">Slot {nextSlot.slot}</strong> : {nextSlot.label}
              </div>
            )}
          </div>
        )}

        {/* Regions panel */}
        {prog && (
          <div className="panel p-3" data-testid="regions-panel">
            <div className="flex justify-between items-center mb-2">
              <h2 className="overline flex items-center gap-1"><Map size={11}/> Régions</h2>
              <span className="font-mono text-[10px] text-[#4A4A4A]">
                {prog.unlocked_regions.length}/{prog.regions.length}
              </span>
            </div>
            <div className="space-y-1">
              {prog.regions.map((r) => (
                <div
                  key={r.id}
                  data-testid={`region-${r.id}`}
                  className={`flex items-center justify-between px-2 py-1 border ${
                    r.unlocked ? "border-[#2E4F32] bg-[#FCF9F2]" : "border-[#7A7A7A] bg-[#EAE4D9] opacity-70"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {r.unlocked ? <Unlock size={11} className="text-[#2E4F32]"/> : <Lock size={11} className="text-[#7A7A7A]"/>}
                    <span className="font-serif-display text-base leading-none">{r.name}</span>
                  </div>
                  <span className="font-mono text-[9px] text-[#4A4A4A]">
                    {r.unlocked ? "DÉBLOQUÉE" : describeCondition(r.unlock_condition)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </section>

      {/* Pending charms banner */}
      {charms.pending.length > 0 && (
        <div className="col-span-12 panel-strong p-4 bg-[#FCF9F2]" data-testid="charm-banner">
          <div className="flex items-start gap-3 flex-wrap">
            <Gem size={28} className="text-[#B87333]"/>
            <div className="flex-1 min-w-0">
              <div className="overline text-[#B87333]">Récompense disponible</div>
              <h3 className="font-serif-display text-2xl leading-none mt-1">
                {charms.pending.length} Charme(s) Chromatique(s) à attribuer
              </h3>
              <p className="text-xs text-[#4A4A4A] mt-1">
                Pokédex complété pour : {charms.pending.map(r => r.toUpperCase()).join(", ")}.
                Choisissez le dresseur qui le recevra (chances de shiny ×3).
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {trainers.filter(t => !t.has_shiny_charm).map((t) => (
                <button
                  key={t.id}
                  className="btn-ink btn-leaf"
                  onClick={() => assignCharm(t.id)}
                  disabled={charmAssigning}
                  data-testid={`charm-assign-${t.id}`}
                >
                  À {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <TrainerDetailModal
        trainerId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        regions={prog?.regions || []}
        initialTab={detailTab}
      />

      <ForceActionModal
        trainerId={forceAction?.id}
        action={forceAction?.action}
        open={!!forceAction}
        onClose={() => setForceAction(null)}
        onAfterSubmit={load}
      />

      <RecruitModal open={recruitOpen} onClose={() => setRecruitOpen(false)} onRecruited={load}/>
    </div>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="panel p-3" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-serif-display text-4xl leading-none mt-1">{value}</div>
      <div className="text-[11px] font-mono text-[#4A4A4A] mt-1">{sub}</div>
    </div>
  );
}
